// SAB-428: Section C — "How It Works".
// Static content lifted from sab428_howitworks_cards_reference.html section C.
// All visual tokens (sabotage gradient, damage gradient, success green,
// rarity tiers) reuse SAB-427's existing palette + the .text-gradient-damage
// utility added there. No new tokens introduced.
//
// SAB-578: the live Trash Scoring card sits side by side with the real Geno Smith
// game card (RealGameCard, moved here from SocialProof) above the step cards. A
// verifiable real box score is stronger proof than a hypothetical. The old
// standalone YOUR ROSTER slot row is gone (step 01 is now the only mention of the
// Coach slot, deliberately).
import { ScoreboardPanel } from './ScoreboardPanel';
import { RealGameCard } from './RealGameCard';

interface StepData {
  num: string;
  title: string;
  body: string;
  highlight?: boolean;
}

const STEPS: StepData[] = [
  { num: '01', title: 'Draft your roster',     body: 'Snake-draft QB / RB / WR / TE / K / DST plus a Coach slot for your league.' },
  { num: '02', title: 'Set your trash lineup', body: 'Target the boom-or-bust spots and the messy matchups. Volatility is the whole point.' },
  { num: '03', title: 'Sabotage opponents',    body: 'Spend credits on cards that amplify your rivals’ disasters mid-week.' },
  { num: '04', title: 'Trash wins',            body: 'Most chaos points takes the pot. Bad has never paid this well.', highlight: true },
];

export function HowItWorks() {
  return (
    <section id="how" className="mx-auto max-w-[1180px] px-6 py-10 sm:py-14">
      {/* SAB-445: section padding 14→10 on mobile (saves ~46px). */}
      {/* Header — SAB-447: hidden on mobile (H2+body merged into Hero w/ scoreboard) */}
      <div className="hidden sm:block text-center max-w-[680px] mx-auto mb-6 sm:mb-8">
        <div className="font-display text-[11px] uppercase tracking-[0.06em] font-bold text-muted-foreground mb-3">
          How it works
        </div>
        <h2
          className="m-0 mb-3.5 font-display font-extrabold"
          style={{ fontSize: 'clamp(1.9rem, 4vw, 2.6rem)' }}
        >
          Wait, the bad plays{' '}
          <span className="text-gradient-damage">score?</span>
        </h2>
        <p className="text-[17px] text-muted-foreground">
          Exactly. We flipped the scoreboard. Touchdowns are boring. The chaos is what pays.
          Turnovers, sacks, fumbles, missed kicks and penalties all rack up points.
        </p>
      </div>

      {/* SAB-578: live Trash Scoring card (illustrative ticker) + the real Geno
          Smith game card, side by side on desktop (live card on the left), stacked
          with the live card first on mobile, above the step cards. */}
      <div className="grid gap-4 sm:gap-5 md:grid-cols-2 items-start max-w-[900px] mx-auto mb-6 sm:mb-9">
        <ScoreboardPanel />
        <RealGameCard />
      </div>

      {/* Desktop: 4 step cards — SAB-447: gated to md+ only. */}
      <div
        className="hidden sm:grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]"
      >
        {STEPS.map((step) => (
          <div
            key={step.num}
            className={
              step.highlight
                ? 'p-4 sm:p-6 rounded-xl bg-card border border-[hsl(25_95%_53%_/_0.4)] shadow-sabotage'
                : 'p-4 sm:p-6 rounded-xl bg-card border border-border'
            }
          >
            <div className="font-varsity text-gradient-sabotage text-[34px] leading-none">{step.num}</div>
            <h3 className="font-display font-extrabold text-foreground mt-2 mb-1.5">{step.title}</h3>
            <p className="text-sm text-muted-foreground m-0">{step.body}</p>
          </div>
        ))}
      </div>

      {/* SAB-447: mobile = 3 steps in a horizontal swipe strip (scroll-snap).
          Indices [0, 2, 3] from STEPS — drops "Set your trash lineup" to
          keep the swipe to the core narrative (Draft → Sabotage → Trash wins).
          Renumbers to 01/02/03 since desktop numbers don't read cleanly with
          a skip. The -mx-6 + px-6 lets the strip bleed to the screen edges
          while keeping content inside the section's px-6 padding. */}
      <div
        className="sm:hidden -mx-6 px-6 flex gap-3 overflow-x-auto snap-x snap-mandatory no-scrollbar"
        style={{ scrollPaddingInline: '1.5rem' }}
      >
        {[STEPS[0], STEPS[2], STEPS[3]].map((step, i) => (
          <div
            key={step.num}
            className={
              (step.highlight
                ? 'border-[hsl(25_95%_53%_/_0.4)] shadow-sabotage '
                : 'border-border ')
              + 'flex-none w-[78%] max-w-[280px] p-4 rounded-xl bg-card border snap-start'
            }
          >
            <div className="font-varsity text-gradient-sabotage text-[34px] leading-none">
              {String(i + 1).padStart(2, '0')}
            </div>
            <h3 className="font-display font-extrabold text-foreground mt-2 mb-1.5">{step.title}</h3>
            <p className="text-sm text-muted-foreground m-0">{step.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export default HowItWorks;
